import Tag from '@/components/ui/custom/dataDisplay/Tag';
import { jsFixGenerator } from '@/utils/blockQuestion';
import { Workspace } from '@cna-br/astro-code';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CodeRun from '../CodeRun';
import { useJSRuntime } from '../codeEditor/CodeEditor.hooks';
import BlockCode from './BlockCode';

export type BlockCodeRuntimeProps = {
  workspace: Workspace | undefined;
  onWorkspaceChange: (workspace: Workspace) => void;
  maxBlocks?: number;
  readOnly?: boolean;
  className?: string;
};

export default function BlockCodeRuntime({
  workspace,
  onWorkspaceChange,
  maxBlocks,
  readOnly,
  className,
}: BlockCodeRuntimeProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'blockCodeRuntime',
  });
  const [output, setOutput] = useState('');
  const [blocks, setBlocks] = useState(0);
  const { run, loading } = useJSRuntime();
  const onRun = async () => {
    if (!workspace) return;
    const code = jsFixGenerator(workspace);
    const result = await run(code);
    setOutput(result);
  };

  const onReset = () => {
    setOutput('');
  };

  const allBlocks = workspace?.getAllBlocks();

  useEffect(() => {
    setBlocks(allBlocks?.length || 0);
  }, [allBlocks, workspace]);

  return (
    <div className="relative flex flex-col gap-2 bg-base-100 rounded-lg p-2 border border-primary/50 w-full">
      <BlockCode
        onWorkspaceChange={onWorkspaceChange}
        workspace={workspace}
        maxBlocks={maxBlocks}
        readOnly={readOnly}
        className={className}
      />
      <CodeRun
        disabled={blocks <= 1}
        isRunning={loading}
        onRun={onRun}
        onReset={onReset}
        output={output}
      />
      <div className="absolute top-4 right-4">
        <Tag className={{ base: 'text-sm gap-1' }}>
          <span>{blocks}</span>
          {t('blocks', {
            count: blocks,
          })}
        </Tag>
      </div>
    </div>
  );
}
